import { useContext } from "react";
import { Modal, Typography, Tag, Space, Button, Row, Col, Card } from "antd";
import {
    FileTextOutlined,
    CalendarOutlined,
    DollarCircleOutlined,
    ShopOutlined,
    UserOutlined,
    LinkOutlined,
    EnvironmentOutlined,
    NumberOutlined,
} from "@ant-design/icons";

import { convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { getLinkInvoice } from "../../../../utils/Functions";
import { IPendingInvoice } from "../../../../interfaces";
import { formatCpf, formatCpfCnpj } from "../../../../utils/Masks";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Paragraph } = Typography;

interface ITableData extends IPendingInvoice {
    rowKey: number;
    createdAt: Date;
}

interface IProps {
    open: boolean;
    onClose: () => void;
    invoice: ITableData | undefined;
}

export default function ModalInvoiceDetails({
    open,
    onClose,
    invoice,
}: IProps) {
    const { theme } = useContext<any>(ThemeContext);
    const isDark = theme === "dark";

    if (!invoice) return null;

    const cardBg = isDark ? "#141414" : "#f8fafc";
    const cardBorder = isDark ? "#303030" : "#e2e8f0";

    const getTypeTag = (type?: string) => {
        if (type === "NFC") return <Tag color="purple" style={{ fontWeight: 700, borderRadius: 4, margin: 0 }}>NFC</Tag>;
        if (type === "NFE") return <Tag color="cyan" style={{ fontWeight: 700, borderRadius: 4, margin: 0 }}>NFE</Tag>;
        return <Tag color="blue" style={{ fontWeight: 700, borderRadius: 4, margin: 0 }}>{type || "NFS"}</Tag>;
    };

    const getStatusTag = (status?: string) => {
        if (!status) return <Tag>—</Tag>;
        const s = status.toLowerCase();
        if (s.includes("conclu") || s.includes("sucess") || s.includes("finaliz")) {
            return <Tag color="success" style={{ fontWeight: 600, borderRadius: 4, margin: 0 }}>{status}</Tag>;
        }
        if (s.includes("recus") || s.includes("erro") || s.includes("cancel")) {
            return <Tag color="error" style={{ fontWeight: 600, borderRadius: 4, margin: 0 }}>{status}</Tag>;
        }
        if (s.includes("pend") || s.includes("aguard") || s.includes("process")) {
            return <Tag color="warning" style={{ fontWeight: 600, borderRadius: 4, margin: 0 }}>{status}</Tag>;
        }
        return <Tag color="default" style={{ fontWeight: 600, borderRadius: 4, margin: 0 }}>{status}</Tag>;
    };

    const invoiceLink = invoice.type ? getLinkInvoice(invoice.type) : null;

    return (
        <Modal
            width={760}
            open={open}
            centered
            destroyOnClose
            title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
                    <Space size={10} align="center">
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: isDark ? "rgba(22, 119, 255, 0.15)" : "#e6f4ff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FileTextOutlined style={{ color: "#1677ff", fontSize: 17 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                                Detalhes da Nota Importada #{invoice.id}
                            </div>
                            <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 1 }}>
                                Dados cadastrais e fiscais do registro importado
                            </div>
                        </div>
                    </Space>
                </div>
            }
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button type="primary" onClick={onClose} style={{ minWidth: 90 }}>
                        Fechar
                    </Button>
                </div>
            }
            onCancel={onClose}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 0" }}>
                <div
                    style={{
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: 8,
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 12,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                background: isDark ? "rgba(82, 196, 26, 0.15)" : "#f6ffed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <DollarCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#64748b" }}>Valor da Nota</div>
                            <div style={{ color: isDark ? "#52c41a" : "#237804", fontSize: 18, fontWeight: 700 }}>
                                R$ {convertMoedaBRDoJSNovo(invoice.value || 0)}
                            </div>
                        </div>
                    </div>

                    <Space size={8} wrap>
                        {getTypeTag(invoice.type)}
                        {getStatusTag(invoice.status)}
                    </Space>
                </div>

                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12}>
                        <Card
                            size="small"
                            style={{
                                background: cardBg,
                                border: `1px solid ${cardBorder}`,
                                borderRadius: 8,
                                height: "100%",
                            }}
                            title={
                                <Space size={6}>
                                    <UserOutlined style={{ color: "#1677ff" }} />
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>Cidadão / Usuário</span>
                                </Space>
                            }
                        >
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12.5 }}>
                                <div>
                                    <span style={{ color: isDark ? "#8c8c8c" : "#64748b" }}>Nome: </span>
                                    <strong style={{ color: isDark ? "#f0f0f0" : "#0f172a" }}>
                                        {invoice.name || "—"}
                                    </strong>
                                </div>
                                {invoice.cpf && (
                                    <div>
                                        <span style={{ color: isDark ? "#8c8c8c" : "#64748b" }}>CPF: </span>
                                        <Tag style={{ fontSize: 11.5, margin: 0, borderRadius: 4 }}>
                                            {formatCpf(invoice.cpf)}
                                        </Tag>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Card
                            size="small"
                            style={{
                                background: cardBg,
                                border: `1px solid ${cardBorder}`,
                                borderRadius: 8,
                                height: "100%",
                            }}
                            title={
                                <Space size={6}>
                                    <ShopOutlined style={{ color: "#722ed1" }} />
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>Empresa / Estabelecimento</span>
                                </Space>
                            }
                        >
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12.5 }}>
                                <div>
                                    <span style={{ color: isDark ? "#8c8c8c" : "#64748b" }}>Razão Social: </span>
                                    <strong style={{ color: isDark ? "#f0f0f0" : "#0f172a" }}>
                                        {invoice.company || "—"}
                                    </strong>
                                </div>
                                {invoice.cpf_cnpj && (
                                    <div>
                                        <span style={{ color: isDark ? "#8c8c8c" : "#64748b" }}>CNPJ/CPF: </span>
                                        <Tag style={{ fontSize: 11.5, margin: 0, borderRadius: 4 }}>
                                            {formatCpfCnpj(invoice.cpf_cnpj)}
                                        </Tag>
                                    </div>
                                )}
                                {invoice.address && (
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                                        <EnvironmentOutlined style={{ color: "#94a3b8", marginTop: 2 }} />
                                        <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 11.5 }}>
                                            {invoice.address}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
                <Card
                    size="small"
                    style={{
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: 8,
                    }}
                    title={
                        <Space size={6}>
                            <NumberOutlined style={{ color: "#fa8c16" }} />
                            <span style={{ fontSize: 13, fontWeight: 600 }}>Identificação Fiscal</span>
                        </Space>
                    }
                >
                    <Row gutter={[16, 10]} style={{ fontSize: 12.5 }}>
                        <Col xs={24} sm={8}>
                            <div style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 11 }}>Número da Nota</div>
                            <strong style={{ color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 13 }}>
                                {invoice.number ?? "—"}
                            </strong>
                        </Col>

                        <Col xs={24} sm={8}>
                            <div style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 11 }}>Data de Emissão</div>
                            <Space size={4} align="center" style={{ marginTop: 2 }}>
                                <CalendarOutlined style={{ color: "#1677ff", fontSize: 12 }} />
                                <span style={{ color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 12.5 }}>
                                    {invoice.date_issue ? formatDateStringToDateAndTime(invoice.date_issue, true) : "—"}
                                </span>
                            </Space>
                        </Col>

                        <Col xs={24} sm={8}>
                            <div style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 11 }}>Data de Criação</div>
                            <span style={{ color: isDark ? "#f0f0f0" : "#0f172a", fontSize: 12.5 }}>
                                {invoice.createdAt ? formatDateStringToDateAndTime(String(invoice.createdAt), true) : "—"}
                            </span>
                        </Col>
                        <Col span={24}>
                            <div style={{ marginTop: 4 }}>
                                <div style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 11, marginBottom: 2 }}>
                                    Chave de Acesso
                                </div>
                                {invoice.key ? (
                                    <div
                                        style={{
                                            background: isDark ? "#1f1f1f" : "#ffffff",
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: 6,
                                            padding: "6px 10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Paragraph
                                            copyable={{ text: String(invoice.key), tooltips: ["Copiar chave", "Copiado!"] }}
                                            style={{
                                                margin: 0,
                                                fontFamily: "monospace",
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: isDark ? "#60a5fa" : "#1d4ed8",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {String(invoice.key)}
                                        </Paragraph>
                                    </div>
                                ) : (
                                    <span style={{ color: isDark ? "#8c8c8c" : "#94a3b8" }}>—</span>
                                )}
                            </div>
                        </Col>
                        {invoiceLink && (
                            <Col span={24}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                                    <LinkOutlined style={{ color: "#1677ff" }} />
                                    <a
                                        href={invoiceLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: 12, color: "#1677ff", fontWeight: 500 }}
                                    >
                                        Acessar Portal da Nota ({invoice.type || "NFS"})
                                    </a>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card>
            </div>
        </Modal>
    );
}
